import { useCallback, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { SETTING_DEFS } from "@gaole/shared";
import { api } from "../lib/api";
import { toast } from "../stores/toast";

export interface SettingRow {
  key: string;
  group: string;
  label: string;
  type: string;
  value: unknown;
  description?: string;
}

function settingsToMap(settings: SettingRow[]) {
  return Object.fromEntries(settings.map((s) => [s.key, s.value]));
}

export const settingsQueryKey = ["settings"] as const;

export function settingsQueryOptions(): UseQueryOptions<SettingRow[]> {
  return {
    queryKey: settingsQueryKey,
    queryFn: () => api<{ settings: SettingRow[] }>("/settings").then((r) => r.settings),
  };
}

/** Read settings from the shared React Query cache (prefetched in Layout). */
export function useSettingsQuery(options?: { enabled?: boolean }) {
  return useQuery({
    ...settingsQueryOptions(),
    enabled: options?.enabled ?? true,
  });
}

export function useSettingsMap(options?: { enabled?: boolean }) {
  const query = useSettingsQuery(options);
  const map = useMemo(
    () => (query.data ? settingsToMap(query.data) : null),
    [query.data],
  );
  return { ...query, map };
}

export function useSettingValue<T>(key: string, fallback: T, options?: { enabled?: boolean }): T {
  const { map } = useSettingsMap(options);
  if (!map || !(key in map)) return fallback;
  return map[key] as T;
}

export function useSettingsDraft(options?: { saveToast?: string }) {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState<Record<string, unknown>>({});

  const query = useSettingsQuery();

  const serverMap = useMemo(
    () => (query.data ? settingsToMap(query.data) : null),
    [query.data],
  );

  const draft = useMemo(
    () => ({ ...(serverMap ?? {}), ...overrides }),
    [serverMap, overrides],
  );

  const setValue = useCallback((key: string, value: unknown) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
  }, []);

  const saveMut = useMutation({
    mutationFn: (keys: string[]) => {
      const values: Record<string, unknown> = {};
      for (const key of keys) {
        const def = SETTING_DEFS.find((d) => d.key === key);
        const raw = draft[key];
        if (def?.type === "number" && typeof raw === "string") {
          values[key] = raw.trim() === "" ? def.defaultValue : Number(raw);
        } else {
          values[key] = raw;
        }
      }
      return api("/settings", { method: "PUT", body: { values } });
    },
    onSuccess: async (_data, keys) => {
      toast("success", options?.saveToast ?? "Settings saved");
      setOverrides((prev) => {
        const next = { ...prev };
        for (const key of keys) delete next[key];
        return next;
      });
      await queryClient.invalidateQueries({ queryKey: settingsQueryKey });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  return {
    draft,
    setValue,
    saveMut,
    settings: query.data,
    isLoading: query.isLoading,
    showLoader: query.isLoading && !query.data,
  };
}
